import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table,
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { inventoryAPI } from '../../services';
import { supplierMaterialAPI, supplierOrderZaloAPI } from '../../services/purchaseOrder';
import { branchAPI } from '../../services/branch';
import { useBranch } from '../../context/BranchContext';
import SupplierAutoOrderConfigCard from '../../components/supplier/SupplierAutoOrderConfigCard';
import SupplierZaloPhoneEditor from '../../components/supplier/SupplierZaloPhoneEditor';
import SupplierZaloPicker from '../../components/supplier/SupplierZaloPicker';
import QtyDual from '../../utils/inventoryUnit';

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('vi-VN') : '—');

const pkgLabel = (si) => si?.package?.unit_label || si?.package?.name || si?.material?.unit || '';

/**
 * AC 20 (#125114) — tin xuất phát từ đơn do BỘ CÀI sinh ra thì cột "Người gửi" hiện
 * `Tự động — <tên bộ cài>` để phân biệt với đơn gửi tay. `sent_by` vẫn lưu đúng người bấm nút
 * gửi thật (ghi vết nội bộ không đổi), đây CHỈ đổi cách hiển thị.
 */
const senderLabel = (h) => {
  if (h?.purchase_order?.is_auto_generated) {
    return `Tự động — ${h.purchase_order.auto_order_config?.name || 'bộ cài đã xoá'}`;
  }
  return h?.sender?.full_name || '—';
};

/**
 * Gửi đơn đặt hàng cho NCC qua Zalo (#145947).
 *
 * Ràng buộc nghiệp vụ quan trọng:
 * - Người dùng phải BẤM mới gửi; không có đường nào tự gửi ngầm.
 * - MỖI CHI NHÁNH MỘT TIN RIÊNG — chọn 3 chi nhánh thì NCC nhận 3 tin tách biệt.
 * - Bắt buộc đi qua màn XEM TRƯỚC (nội dung thật + SĐT thật) rồi mới có nút gửi.
 * - Số lượng lần này do người dùng nhập/sửa (số đề xuất chỉ là gợi ý ban đầu); chỗ prefill
 *   `suggested_packages` chính là điểm cắm nguồn số tự động sau này.
 */
export default function SendSupplierOrderZaloPage() {
  const navigate = useNavigate();
  const { branches } = useBranch();
  const [searchParams] = useSearchParams();

  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState(searchParams.get('supplier_id') || '');
  // #125114 — nguồn chi nhánh cho bộ cài tự động: đọc thẳng từ /branches/active/simple,
  // KHÔNG hardcode; mở chi nhánh thứ 4 là tự có mặt trong danh sách tick chọn.
  const [autoOrderBranches, setAutoOrderBranches] = useState([]);
  const [autoOrderConfigs, setAutoOrderConfigs] = useState([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);

  // { [branchId]: { loading, items: [...], error } } — NVL của NCC kèm tồn kho theo từng chi nhánh
  const [branchData, setBranchData] = useState({});
  // { [branchId]: { [materialId]: { checked, quantity } } }
  const [selection, setSelection] = useState({});
  const [notes, setNotes] = useState({});

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null);

  const activeBranches = useMemo(
    () => (branches || []).filter((b) => b.is_active !== false),
    [branches],
  );
  const supplier = useMemo(
    () => suppliers.find((s) => s.id === supplierId) || null,
    [suppliers, supplierId],
  );

  useEffect(() => {
    inventoryAPI
      .getSuppliers()
      .then((res) => setSuppliers(Array.isArray(res) ? res : res?.data || []))
      .catch(() => toast.error('Không thể tải danh sách nhà cung cấp'));

    branchAPI
      .getActiveSimple()
      .then((res) => setAutoOrderBranches(Array.isArray(res) ? res : res?.data || []))
      .catch(() => toast.error('Không tải được danh sách chi nhánh đang hoạt động'));
  }, []);

  /** Cập nhật NCC tại chỗ sau khi sửa/xoá SĐT Zalo — dùng ngay cho lần gửi kế tiếp (AC 22). */
  const handleSupplierPatched = useCallback((patched) => {
    setSuppliers((prev) => prev.map((s) => (s.id === patched.id ? { ...s, ...patched } : s)));
  }, []);

  const loadHistory = useCallback(async (sid) => {
    if (!sid) { setHistory([]); return; }
    try {
      setHistoryLoading(true);
      const res = await supplierOrderZaloAPI.history({ supplier_id: sid, limit: 50 });
      setHistory(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      toast.error(err?.message || 'Không tải được lịch sử gửi');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(supplierId); }, [supplierId, loadHistory]);

  // Đổi NCC → xoá sạch lựa chọn cũ để không mang số lượng của NCC trước sang NCC sau
  const handleSupplierChange = (id) => {
    setSupplierId(id);
    setSelectedBranchIds([]);
    setBranchData({});
    setSelection({});
    setNotes({});
    setPreview(null);
    setSendResult(null);
  };

  const loadBranchItems = useCallback(async (sid, branchId) => {
    setBranchData((prev) => ({ ...prev, [branchId]: { loading: true, items: [], error: null } }));
    try {
      const res = await supplierMaterialAPI.listBySupplier(sid, branchId);
      const items = Array.isArray(res) ? res : res?.data || [];
      setBranchData((prev) => ({ ...prev, [branchId]: { loading: false, items, error: null } }));
      // Prefill: tick sẵn NVL dưới ngưỡng, số lượng = số đề xuất (điểm cắm nguồn số tự động sau này)
      setSelection((prev) => ({
        ...prev,
        [branchId]: items.reduce((acc, si) => {
          const suggested = Number(si.suggested_packages) > 0 ? Number(si.suggested_packages) : 1;
          acc[si.material_id] = { checked: !!si.is_low_stock, quantity: suggested };
          return acc;
        }, {}),
      }));
    } catch (err) {
      setBranchData((prev) => ({
        ...prev,
        [branchId]: { loading: false, items: [], error: err?.message || 'Không tải được NVL' },
      }));
    }
  }, []);

  const toggleBranch = (branchId) => {
    setPreview(null);
    setSendResult(null);
    setSelectedBranchIds((prev) => {
      if (prev.includes(branchId)) return prev.filter((b) => b !== branchId);
      if (supplierId) loadBranchItems(supplierId, branchId);
      return [...prev, branchId];
    });
  };

  const setItem = (branchId, materialId, patch) => {
    setPreview(null);
    setSelection((prev) => ({
      ...prev,
      [branchId]: {
        ...(prev[branchId] || {}),
        [materialId]: { ...(prev[branchId]?.[materialId] || {}), ...patch },
      },
    }));
  };

  // Payload chung cho cả preview và send — dựng 1 chỗ để 2 luồng không lệch nhau
  const buildPayload = () => {
    const branchesPayload = selectedBranchIds
      .map((branchId) => {
        const items = (branchData[branchId]?.items || [])
          .filter((si) => selection[branchId]?.[si.material_id]?.checked)
          .map((si) => ({
            material_id: si.material_id,
            package_id: si.package?.id || undefined,
            quantity: Number(selection[branchId][si.material_id].quantity),
          }))
          .filter((i) => i.quantity > 0);
        return { branch_id: branchId, note: notes[branchId]?.trim() || undefined, items };
      })
      .filter((b) => b.items.length > 0);

    return { supplier_id: supplierId, branches: branchesPayload };
  };

  const handlePreview = async () => {
    if (!supplierId) { toast.error('Vui lòng chọn nhà cung cấp'); return; }
    const payload = buildPayload();
    if (!payload.branches.length) {
      toast.error('Chọn ít nhất 1 NVL (số lượng > 0) cho ít nhất 1 chi nhánh');
      return;
    }
    try {
      setPreviewing(true);
      setSendResult(null);
      const res = await supplierOrderZaloAPI.preview(payload);
      setPreview({ ...res, payload });
    } catch (err) {
      toast.error(err?.message || 'Không dựng được nội dung xem trước');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!preview?.payload) return;
    try {
      setSending(true);
      const res = await supplierOrderZaloAPI.send(preview.payload);
      setSendResult(res);
      if (res.failed > 0) {
        // CẤM nuốt lỗi: có tin nào hỏng là phải nói rõ, không báo thành công chung chung
        toast.error(`Gửi xong: ${res.sent} thành công, ${res.failed} THẤT BẠI — xem chi tiết lỗi bên dưới`);
      } else {
        toast.success(`Đã gửi ${res.sent} tin đơn đặt hàng qua Zalo`);
      }
      setPreview(null);
      loadHistory(supplierId);
    } catch (err) {
      toast.error(err?.message || 'Không gửi được đơn qua Zalo');
    } finally {
      setSending(false);
    }
  };

  const totalCheckedItems = selectedBranchIds.reduce(
    (sum, bId) =>
      sum + Object.values(selection[bId] || {}).filter((v) => v.checked && Number(v.quantity) > 0).length,
    0,
  );

  return (
    <PageLayout>
      <div className="container-fluid py-3">
        <div className="d-flex align-items-center gap-3 mb-4">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>← Quay lại</Button>
          <h4 className="mb-0 fw-bold">Gửi đơn đặt hàng cho NCC qua Zalo</h4>
        </div>

        <Row className="g-3">
          <Col lg={7}>
            <Card className="shadow-sm mb-3">
              <Card.Header className="fw-semibold bg-white">1. Chọn nhà cung cấp</Card.Header>
              <Card.Body>
                {/* #233007 — ô chọn NCC có tìm kiếm, nhóm "đã có SĐT Zalo" đứng trước và nhãn
                    CHỮ cho NCC chưa có số, để không chọn nhầm rồi bấm Gửi mới biết không gửi được.
                    Cảnh báo đỏ + nút thêm số ngay lúc chọn vẫn do `SupplierZaloPhoneEditor` ngay
                    bên dưới lo (US-115332 mục E) — kế thừa, không viết lại. */}
                <SupplierZaloPicker
                  suppliers={suppliers}
                  value={supplierId}
                  onChange={handleSupplierChange}
                />

                {/* #125114 mục E — sửa/xoá/thêm SĐT Zalo INLINE ngay tại đây, không vòng qua
                    màn danh sách NCC. Ghi đúng `Supplier.zalo_phone` qua PATCH /suppliers/:id. */}
                <SupplierZaloPhoneEditor
                  supplier={supplier}
                  activeAutoConfigs={autoOrderConfigs.filter((c) => c.is_active)}
                  onSaved={handleSupplierPatched}
                />
              </Card.Body>
            </Card>

            <Card className="shadow-sm mb-3">
              <Card.Header className="fw-semibold bg-white">
                2. Chọn chi nhánh <span className="text-muted fw-normal small">— mỗi chi nhánh gửi 1 tin riêng</span>
              </Card.Header>
              <Card.Body>
                {!supplierId && <div className="text-muted small">Chọn nhà cung cấp trước.</div>}
                {supplierId && (
                  <div className="d-flex flex-wrap gap-3">
                    {activeBranches.map((b) => (
                      <Form.Check
                        key={b.id}
                        type="checkbox"
                        id={`branch-${b.id}`}
                        label={b.name}
                        checked={selectedBranchIds.includes(b.id)}
                        onChange={() => toggleBranch(b.id)}
                      />
                    ))}
                    {activeBranches.length === 0 && (
                      <div className="text-muted small">Không có chi nhánh đang hoạt động.</div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* #125114 mục D — KHỐI NHÚNG bộ cài tự động đặt hàng, đặt giữa "2. Chọn chi nhánh"
                và "Lịch sử gửi Zalo" (đúng vùng sếp khoanh đỏ). KHÔNG phải màn cấu hình riêng. */}
            <SupplierAutoOrderConfigCard
              supplierId={supplierId}
              supplierName={supplier?.name}
              branches={autoOrderBranches}
              onConfigsChange={setAutoOrderConfigs}
            />

            {selectedBranchIds.map((branchId) => {
              const branch = activeBranches.find((b) => b.id === branchId);
              const data = branchData[branchId] || {};
              return (
                <Card className="shadow-sm mb-3" key={branchId}>
                  <Card.Header className="fw-semibold bg-white d-flex align-items-center gap-2">
                    <span>3. NVL cần đặt — {branch?.name || branchId}</span>
                    <Badge bg="secondary" className="ms-auto fw-normal">1 tin</Badge>
                  </Card.Header>
                  <Card.Body>
                    {data.loading && <div className="text-center py-3"><Spinner size="sm" /></div>}
                    {data.error && <Alert variant="warning" className="py-2 mb-0">{data.error}</Alert>}
                    {!data.loading && !data.error && (data.items || []).length === 0 && (
                      <div className="text-muted small">
                        NCC này chưa được gán NVL nào. Vào <em>Nhà cung cấp → Quản lý NVL</em> để gán.
                      </div>
                    )}
                    {(data.items || []).length > 0 && (
                      <>
                        <Table size="sm" bordered hover responsive className="mb-3">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 36 }}></th>
                              <th>NVL</th>
                              <th className="text-end">Tồn</th>
                              <th className="text-end" style={{ width: 150 }}>Số lượng đặt</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.items.map((si) => {
                              const sel = selection[branchId]?.[si.material_id] || {};
                              return (
                                <tr key={si.material_id} className={si.is_low_stock ? 'table-danger' : ''}>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      data-testid={`pick-${branchId}-${si.material_id}`}
                                      checked={!!sel.checked}
                                      onChange={(e) => setItem(branchId, si.material_id, { checked: e.target.checked })}
                                    />
                                  </td>
                                  <td>
                                    {si.material?.name}
                                    {si.is_low_stock && <Badge bg="danger" className="ms-2">Sắp hết</Badge>}
                                  </td>
                                  <td className="text-end">
                                    {/* #112414 — Tồn hiện theo ĐÚNG đơn vị đặt hàng của cột
                                        "Số lượng đặt" (`si.package`, cùng nguồn với `pkgLabel`),
                                        dòng phụ giữ nguyên số gốc ml/gam. NVL chưa khai quy cách
                                        (chỉ có đơn vị cơ sở) thì `QtyDual` tự lùi về đơn vị gốc. */}
                                    <QtyDual
                                      quantityInBase={si.current_stock}
                                      material={si.material}
                                      packageOverride={si.package}
                                    />
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center gap-1">
                                      <Form.Control
                                        type="number"
                                        min="0"
                                        step="any"
                                        size="sm"
                                        className="text-end"
                                        data-testid={`qty-${branchId}-${si.material_id}`}
                                        value={sel.quantity ?? ''}
                                        onChange={(e) => setItem(branchId, si.material_id, { quantity: e.target.value })}
                                      />
                                      <small className="text-muted text-nowrap">{pkgLabel(si)}</small>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>

                        <Form.Group>
                          <Form.Label className="small text-muted mb-1">Ghi chú gửi kèm (chi nhánh này)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            data-testid={`note-${branchId}`}
                            value={notes[branchId] || ''}
                            onChange={(e) => {
                              setPreview(null);
                              setNotes((prev) => ({ ...prev, [branchId]: e.target.value }));
                            }}
                          />
                        </Form.Group>
                      </>
                    )}
                  </Card.Body>
                </Card>
              );
            })}

            {selectedBranchIds.length > 0 && (
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="text-muted small">
                  Đang chọn <strong>{totalCheckedItems}</strong> dòng NVL trên{' '}
                  <strong>{selectedBranchIds.length}</strong> chi nhánh.
                </div>
                <Button
                  variant="primary"
                  className="ms-auto"
                  data-testid="btn-preview"
                  disabled={previewing || !supplierId}
                  onClick={handlePreview}
                >
                  {previewing ? 'Đang dựng nội dung...' : 'Xem trước nội dung tin'}
                </Button>
              </div>
            )}

            {sendResult && (
              <Card className="shadow-sm mb-3" data-testid="send-result">
                <Card.Header className="fw-semibold bg-white">Kết quả gửi</Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <Badge bg="success" className="me-2">{sendResult.sent} thành công</Badge>
                    <Badge bg={sendResult.failed ? 'danger' : 'secondary'}>{sendResult.failed} thất bại</Badge>
                  </div>
                  <Table size="sm" bordered className="mb-0">
                    <thead className="table-light">
                      <tr><th>Chi nhánh</th><th>SĐT</th><th>Kết quả</th></tr>
                    </thead>
                    <tbody>
                      {sendResult.results.map((r) => (
                        <tr key={r.message_id}>
                          <td>{r.branch_name}</td>
                          <td>{r.phone}</td>
                          <td>
                            {r.ok ? (
                              <Badge bg="success">Đã gửi</Badge>
                            ) : (
                              <>
                                <Badge bg="danger" className="me-2">Thất bại</Badge>
                                <span className="text-danger small" data-testid={`err-${r.branch_id}`}>{r.error}</span>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col lg={5}>
            <Card className="shadow-sm">
              <Card.Header className="fw-semibold bg-white d-flex align-items-center">
                <span>Lịch sử gửi Zalo</span>
                <Button
                  variant="link"
                  size="sm"
                  className="ms-auto p-0"
                  data-testid="btn-reload-history"
                  onClick={() => loadHistory(supplierId)}
                >
                  Tải lại
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {historyLoading && <div className="text-center py-3"><Spinner size="sm" /></div>}
                {!historyLoading && history.length === 0 && (
                  <div className="text-muted small p-3">
                    {supplierId ? 'Chưa gửi tin nào cho NCC này.' : 'Chọn NCC để xem lịch sử.'}
                  </div>
                )}
                {!historyLoading && history.length > 0 && (
                  <Table size="sm" hover responsive className="mb-0" data-testid="history-table">
                    <thead className="table-light">
                      <tr><th>Thời gian</th><th>Chi nhánh</th><th>Người gửi</th><th>Kết quả</th></tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} style={{ cursor: 'pointer' }} onClick={() => setHistoryDetail(h)}>
                          <td className="small">{fmtDateTime(h.created_at)}</td>
                          <td className="small">{h.branch?.name || '—'}</td>
                          <td className="small">{senderLabel(h)}</td>
                          <td>
                            {h.status === 'SENT'
                              ? <Badge bg="success">Đã gửi</Badge>
                              : <Badge bg="danger" title={h.error || ''}>Thất bại</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* XEM TRƯỚC — bắt buộc qua bước này mới có nút gửi thật */}
      <Modal show={!!preview} onHide={() => setPreview(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Xem trước — {preview?.messages?.length || 0} tin sẽ gửi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="py-2">
            Sẽ gửi <strong>{preview?.messages?.length || 0}</strong> tin Zalo tới số{' '}
            <strong data-testid="preview-phone">{preview?.target_phone || '(chưa có SĐT Zalo)'}</strong>{' '}
            của NCC <strong>{preview?.supplier?.name}</strong>. Mỗi chi nhánh là một tin riêng.
          </Alert>
          {(preview?.messages || []).map((m) => (
            <Card className="mb-3" key={m.branch_id}>
              <Card.Header className="py-2 fw-semibold small bg-white">{m.branch_name}</Card.Header>
              <Card.Body className="py-2">
                <pre
                  className="mb-0 small"
                  style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                  data-testid={`preview-${m.branch_id}`}
                >
                  {m.content}
                </pre>
              </Card.Body>
            </Card>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreview(null)} disabled={sending}>Huỷ</Button>
          <Button
            variant="danger"
            data-testid="btn-confirm-send"
            disabled={sending || !preview?.target_phone}
            onClick={handleSend}
          >
            {sending ? 'Đang gửi...' : `Xác nhận & gửi ${preview?.messages?.length || 0} tin`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Chi tiết 1 tin trong lịch sử — mở ra là biết ngay tin đó của chi nhánh nào, nội dung gì */}
      <Modal show={!!historyDetail} onHide={() => setHistoryDetail(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Tin đã gửi — {historyDetail?.branch?.name} · {fmtDateTime(historyDetail?.created_at)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2 small text-muted">
            NCC: <strong>{historyDetail?.supplier?.name}</strong> · SĐT: <strong>{historyDetail?.phone}</strong>
            {' '}· Người gửi: <strong>{senderLabel(historyDetail)}</strong>
            {historyDetail?.purchase_order?.is_auto_generated && (
              <> · Người bấm nút: <strong>{historyDetail?.sender?.full_name || '—'}</strong></>
            )}
            {historyDetail?.purchase_order?.order_number && (
              <> · Đơn: <strong>{historyDetail.purchase_order.order_number}</strong></>
            )}
          </div>
          {historyDetail?.status === 'FAILED' && (
            <Alert variant="danger" className="py-2 small" data-testid="history-error">
              Gửi thất bại: {historyDetail?.error || 'không rõ lý do'}
            </Alert>
          )}
          <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {historyDetail?.content}
          </pre>
        </Modal.Body>
      </Modal>
    </PageLayout>
  );
}
